import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { uploadFiles, getRoles } from '../api/api';

const UploadPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [jobRequirements, setJobRequirements] = useState('');
  const [weights, setWeights] = useState({
    years_of_experience: 20,
    role_relevant_experience: 30,
    education: 10,
    tech_stack: 30,
    loyalty: 10,
  });
  const [thresholds, setThresholds] = useState({
    years_of_experience: 10,
    role_relevant_experience: 5,
    loyalty: 5,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [error, setError] = useState(null);

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await getRoles();
        setRoles(data);
      } catch (err) {
        console.error('Error fetching roles:', err);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Handle role selection
  const handleRoleSelect = (event) => {
    const roleId = event.target.value;
    setSelectedRoleId(roleId);
    
    if (roleId === 'custom') {
      setShowCustomForm(true);
    } else if (roleId) {
      setShowCustomForm(false);
      // Find the selected role
      const selectedRole = roles.find(role => role.id.toString() === roleId);
      if (selectedRole) {
        // Populate form with role data
        setJobTitle(selectedRole.title);
        setJobRequirements(selectedRole.requirements || '');
        
        // Extract weights and thresholds from role's scoring criteria
        const newWeights = {};
        const newThresholds = {};
        
        Object.entries(selectedRole.scoring_criteria).forEach(([key, value]) => {
          newWeights[key] = Math.round(value.weight * 100);
          if (value.threshold) {
            newThresholds[key] = value.threshold;
          }
        });
        
        setWeights(newWeights);
        setThresholds(newThresholds);
      }
    } else {
      setShowCustomForm(false);
    }
  };

  // Handle file drop
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: (acceptedFiles) => {
      setFiles([...files, ...acceptedFiles]);
    },
  });

  // Remove file from list
  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  // Handle weight change
  const handleWeightChange = (name, value) => {
    setWeights({
      ...weights,
      [name]: parseInt(value, 10),
    });
  };

  // Handle threshold change
  const handleThresholdChange = (name, value) => {
    setThresholds({
      ...thresholds,
      [name]: parseInt(value, 10),
    });
  };
  
  // Switch to custom form
  const handleUseCustomForm = () => {
    setSelectedRoleId('custom');
    setShowCustomForm(true);
  };

  // Validate total weight is 100%
  const isTotalWeightValid = () => {
    const total = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
    return total === 100;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedRoleId === '') {
      setError('Please select a role or create a custom one');
      return;
    }

    if (showCustomForm && !jobTitle) {
      setError('Job title is required');
      return;
    }

    if (!files.length) {
      setError('Please upload at least one file');
      return;
    }

    if (showCustomForm && !isTotalWeightValid()) {
      setError('Total weight must be 100%');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      
      // If using a predefined role, send the role ID
      if (selectedRoleId && selectedRoleId !== 'custom') {
        formData.append('role_id', selectedRoleId);
      } else {
        // Otherwise send custom job details
        formData.append('job_title', jobTitle);
        formData.append('job_requirements', jobRequirements);
      }
      
      // Add weights and thresholds (only for custom form)
      if (showCustomForm) {
        Object.entries(weights).forEach(([key, value]) => {
          formData.append(`${key}_weight`, value / 100);
        });
        
        Object.entries(thresholds).forEach(([key, value]) => {
          formData.append(`${key}_threshold`, value);
        });
      }

      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await uploadFiles(formData);
      navigate(`/evaluation/${response.batch_id}`);
    } catch (err) {
      setError(err.detail || 'Error uploading files. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Upload CVs for Evaluation</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Role selection */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Select Job Role</h2>
            
            {isLoadingRoles ? (
              <div className="flex items-center justify-center p-6">
                <svg
                  className="animate-spin h-8 w-8 text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="ml-3 text-gray-600">Loading roles...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <div className="mb-4">
                  <label htmlFor="roleSelect" className="block text-sm font-medium text-gray-700 mb-1">
                    Choose a Job Role*
                  </label>
                  <select
                    id="roleSelect"
                    value={selectedRoleId}
                    onChange={handleRoleSelect}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select a role --</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>{role.title}</option>
                    ))}
                    <option value="custom">Create custom role</option>
                  </select>
                </div>
                
                {!showCustomForm && roles.length > 0 && selectedRoleId && selectedRoleId !== 'custom' && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h3 className="text-md font-medium text-gray-700 mb-2">
                      {roles.find(r => r.id.toString() === selectedRoleId)?.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {roles.find(r => r.id.toString() === selectedRoleId)?.requirements || 'No requirements specified'}
                    </p>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {Object.entries(roles.find(r => r.id.toString() === selectedRoleId)?.scoring_criteria || {}).map(([key, value]) => (
                        <div key={key} className="text-xs bg-white p-2 rounded shadow-sm">
                          <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                          <span className="text-gray-600">{Math.round(value.weight * 100)}%</span>
                          {value.threshold && (
                            <span className="text-gray-500 text-xs block">Threshold: {value.threshold}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-right">
                      <button 
                        type="button"
                        onClick={handleUseCustomForm}
                        className="text-primary-600 text-sm hover:text-primary-800"
                      >
                        Use custom criteria instead
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Custom job details - shown only when custom is selected */}
          {showCustomForm && (
            <div className="mb-6 border-t pt-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Custom Job Details</h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title*
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Senior Python Developer"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="jobRequirements" className="block text-sm font-medium text-gray-700 mb-1">
                    Job Requirements
                  </label>
                  <textarea
                    id="jobRequirements"
                    value={jobRequirements}
                    onChange={(e) => setJobRequirements(e.target.value)}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., 5+ years Python experience, Knowledge of Django, etc."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Scoring weights - only shown for custom form */}
          {showCustomForm && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-700">Scoring Criteria</h2>
                <div className={`text-sm ${isTotalWeightValid() ? 'text-green-600' : 'text-red-600'} font-medium`}>
                  Total: {Object.values(weights).reduce((sum, weight) => sum + weight, 0)}%
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Years of Experience */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="years_of_experience" className="text-sm font-medium text-gray-700">
                      Years of Experience
                    </label>
                    <span className="text-sm text-gray-500">{weights.years_of_experience}%</span>
                  </div>
                  <input
                    type="range"
                    id="years_of_experience"
                    min="0"
                    max="100"
                    value={weights.years_of_experience}
                    onChange={(e) => handleWeightChange('years_of_experience', e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-2">
                    <label htmlFor="years_threshold" className="text-xs text-gray-500 block">
                      Threshold for full score:
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        id="years_threshold"
                        min="1"
                        max="30"
                        value={thresholds.years_of_experience}
                        onChange={(e) => handleThresholdChange('years_of_experience', e.target.value)}
                        className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                      />
                      <span className="ml-2 text-xs text-gray-500">years</span>
                    </div>
                  </div>
                </div>

                {/* Role-Relevant Experience */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="role_relevant_experience" className="text-sm font-medium text-gray-700">
                      Role-Relevant Experience
                    </label>
                    <span className="text-sm text-gray-500">{weights.role_relevant_experience}%</span>
                  </div>
                  <input
                    type="range"
                    id="role_relevant_experience"
                    min="0"
                    max="100"
                    value={weights.role_relevant_experience}
                    onChange={(e) => handleWeightChange('role_relevant_experience', e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-2">
                    <label htmlFor="role_threshold" className="text-xs text-gray-500 block">
                      Threshold for full score:
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        id="role_threshold"
                        min="1"
                        max="20"
                        value={thresholds.role_relevant_experience}
                        onChange={(e) => handleThresholdChange('role_relevant_experience', e.target.value)}
                        className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                      />
                      <span className="ml-2 text-xs text-gray-500">years</span>
                    </div>
                  </div>
                </div>

                {/* Education */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="education" className="text-sm font-medium text-gray-700">
                      Education
                    </label>
                    <span className="text-sm text-gray-500">{weights.education}%</span>
                  </div>
                  <input
                    type="range"
                    id="education"
                    min="0"
                    max="100"
                    value={weights.education}
                    onChange={(e) => handleWeightChange('education', e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">PhD = 100%, MSc/MBA = 50%, BSc = 30%</p>
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="tech_stack" className="text-sm font-medium text-gray-700">
                      Tech Stack
                    </label>
                    <span className="text-sm text-gray-500">{weights.tech_stack}%</span>
                  </div>
                  <input
                    type="range"
                    id="tech_stack"
                    min="0"
                    max="100"
                    value={weights.tech_stack}
                    onChange={(e) => handleWeightChange('tech_stack', e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">Based on match with required skills</p>
                  </div>
                </div>

                {/* Loyalty */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="loyalty" className="text-sm font-medium text-gray-700">
                      Loyalty
                    </label>
                    <span className="text-sm text-gray-500">{weights.loyalty}%</span>
                  </div>
                  <input
                    type="range"
                    id="loyalty"
                    min="0"
                    max="100"
                    value={weights.loyalty}
                    onChange={(e) => handleWeightChange('loyalty', e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-2">
                    <label htmlFor="loyalty_threshold" className="text-xs text-gray-500 block">
                      Threshold for full score:
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        id="loyalty_threshold"
                        min="1"
                        max="15"
                        value={thresholds.loyalty}
                        onChange={(e) => handleThresholdChange('loyalty', e.target.value)}
                        className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                      />
                      <span className="ml-2 text-xs text-gray-500">years at same company</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* File upload */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Upload CVs</h2>
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 transition-colors"
            >
              <input {...getInputProps()} />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-gray-600 text-center">
                Drag & drop CV files here, or click to select files
                <br />
                <span className="text-xs text-gray-500">(Accepts PDF, DOC, DOCX files)</span>
              </p>
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({files.length})</h3>
                <ul className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-60 overflow-y-auto">
                  {files.map((file, index) => (
                    <li key={index} className="py-3 px-4 flex justify-between items-center hover:bg-gray-50">
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm text-gray-600 truncate" style={{ maxWidth: '15rem' }}>
                          {file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className={`bg-primary-600 text-white py-2 px-6 rounded-md font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                'Start Evaluation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadPage;
